import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Battery, Zap, Recycle, Wrench, Settings, BookOpen, Car } from 'lucide-react';
import { mockData } from '../mock';
import { apiService } from '../services/api';
import LoadingSpinner from './LoadingSpinner';

const moduleIcons = {
  'A': Zap,
  'B': Battery, 
  'C': Battery,
  'D': Recycle,
  'E': Wrench,
  'F': Settings,
  'G': Car
};

const CourseModules = () => {
  const [expandedModules, setExpandedModules] = useState(new Set(['A']));
  const [courseModules, setCourseModules] = useState(mockData.courseModules);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Load course modules from backend
    const loadCourses = async () => {
      try {
        const modules = await apiService.getCourses();
        setCourseModules(modules);
      } catch (err) {
        console.log('Using default course modules:', err.message);
        // Keep default modules on error
      } finally {
        setIsLoading(false);
      }
    };
    
    loadCourses();
  }, []);
  
  const toggleModule = (moduleId) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    setExpandedModules(newExpanded);
  };
  
  if (isLoading) {
    return (
      <section id="curriculum" className="pad-2xl" style={{ backgroundColor: 'var(--bg-section)' }}>
        <div className="container">
          <div className="text-center">
            <LoadingSpinner size="large" />
            <p className="body-medium mt-4">Loading course curriculum...</p>
          </div>
        </div>
      </section>
    );
  }
  
  return (
    <section id="curriculum" className="pad-2xl" style={{ backgroundColor: 'var(--bg-section)' }}>
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="heading-1 mb-4">Complete Course Curriculum</h2>
          <p className="body-large max-w-2xl mx-auto">
            Master battery pack construction through our comprehensive 7-module program, 
            from basic electronics to advanced EV battery analysis.
          </p>
        </div>
        
        <div className="max-w-4xl mx-auto">
          {courseModules.map((module) => {
            const IconComponent = moduleIcons[module.id] || BookOpen;
            const isExpanded = expandedModules.has(module.id);
            
            return (
              <div key={module.id} className={`voice-card ${module.color} mb-6 hover-lift`}>
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleModule(module.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-white rounded-full">
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="mono-text mb-1">MODULE {module.id}</div>
                      <h3 className="heading-3">{module.title}</h3>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="caption">{module.lessons.length} Lessons</div>
                    {isExpanded ? 
                      <ChevronUp className="w-5 h-5" /> : 
                      <ChevronDown className="w-5 h-5" />
                    }
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="mt-6 pt-6 border-t border-light">
                    <div className="grid md:grid-cols-2 gap-3">
                      {module.lessons.map((lesson, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></div>
                          <span className="body-small">{lesson}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Course Highlights */}
        <div className="mt-12 text-center">
          <div className="voice-card accent-grey max-w-2xl mx-auto">
            <h3 className="heading-3 mb-4">🌟 Special Focus: 18650 Cell Recycling</h3>
            <p className="body-medium mb-4">
              Our unique approach teaches you to recycle and repurpose 18650 cells, 
              reducing battery pack costs by up to 70% while maintaining professional quality.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <div className="text-center">
                <div className="heading-2 text-green-600 mb-1">70%</div>
                <div className="caption">Cost Reduction</div>
              </div>
              <div className="text-center">
                <div className="heading-2 text-blue-600 mb-1">100%</div>
                <div className="caption">Practical Skills</div>
              </div>
              <div className="text-center">
                <div className="heading-2 text-purple-600 mb-1">{courseModules.length}</div>
                <div className="caption">Modules</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CourseModules;